import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GetReleaseIssueRequest, GetReleaseIssueResponse, ProductName, ReleaseIssue, ReleaseIssueStatus, ReleaseItem, SituViewWebApiClient, UpdateReleaseIssueRequest, UpdateReleaseIssueResponse, GetSituViewResponse, GetSituViewRequest, CreateSituFacadeRequest, ArtifactEnvironment, EnvironmentName, CreateSituViewRequest, GetSituFacadeResponse, RenderedOutputStatus } from 'situview-webapi-client';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
//import "@google/model-viewer/dist/model-viewer";

// Status enum for rendering process
export enum RenderStatus {
  Idle = 'idle',
  InProgress = 'inProgress',
  Success = 'success',
  Failed = 'failed'
}

export interface QuickViewDialogResult {
  refreshRequired: boolean;
}

export interface QuickViewDialogData {
  quickViewProductName: ProductName | undefined;
  quickViewLink: string | undefined;
  showHistory: boolean | undefined;
  title: string | undefined;
  companyId: string | undefined;
  releaseIssueId: string | undefined;
}

@Component({
  selector: 'quick-view-dialog',
  templateUrl: './quick-view-dialog.component.html',
  styleUrls: ['./quick-view-dialog.component.scss']
})
export class QuickViewDialogComponent implements AfterViewInit, OnInit, OnDestroy {
  productNames = ProductName;
  quickViewReportedSituLinkParams: { [key: string]: string } | undefined;

  viewerWidth: number = 800;
  viewerHeight: number = 600;
  @ViewChild('modelContainer') modelContainer: ElementRef | undefined;
  @ViewChild(MatSort) sort!: MatSort;

  releaseIssue: ReleaseIssue | undefined;
  releaseItemsDataSource: MatTableDataSource<ReleaseItem> = new MatTableDataSource<ReleaseItem>([]);
  isLoadingIssue: boolean = false;
  isDrawerOpen: boolean = false;
  displayedColumns: string[] = ['home', 'options', 'comments', 'releaseStatus', 'actions'];
  releaseIssueStatuses = ReleaseIssueStatus;
  
  // New properties for polling and status tracking
  renderStatus: RenderStatus = RenderStatus.Idle;
  isChecking: boolean = false;
  pollingInterval: any = null;
  currentRenderingId: string | null = null;
  maxPollingAttempts: number = 60; // 5 minutes total (with 5 second interval)
  currentPollingAttempt: number = 0;
  
  private resizeObserver: ResizeObserver | null = null;
  
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: QuickViewDialogData,
    private dialogRef: MatDialogRef<QuickViewDialogData>,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private situViewClient: SituViewWebApiClient) {
      this.quickViewReportedSituLinkParams = this.getQueryParamsFromUrl(this.data.quickViewLink!);
  }

  ngOnInit(): void {
    this.loadReleaseIssue();
  }

  ngAfterViewInit(): void {
    // Initial size calculation
    this.calculateViewerSize();
    
    // Setup ResizeObserver for responsive sizing
    if (this.modelContainer && 'ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(entries => {
        // We only have one observed element
        this.calculateViewerSize();
      });
      
      this.resizeObserver.observe(this.modelContainer.nativeElement);
    } else {
      // Fallback for browsers that don't support ResizeObserver
      window.addEventListener('resize', this.handleResize);
    }
  }
  
  ngOnDestroy(): void {
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    // Remove event listener
    window.removeEventListener('resize', this.handleResize);
    
    // Clean up polling interval if it exists
    this.clearPollingInterval();
  }
  
  private handleResize = (): void => {
    this.calculateViewerSize();
  };

  private calculateViewerSize(): void {
    if (this.modelContainer) {
      const container = this.modelContainer.nativeElement;
      const containerRect = container.getBoundingClientRect();
      
      // Apply a margin to avoid touching edges
      this.viewerWidth = Math.floor(containerRect.width) - 20;
      this.viewerHeight = Math.floor(containerRect.height) - 20;
      
      // Ensure minimum dimensions
      this.viewerWidth = Math.max(this.viewerWidth, 400);
      this.viewerHeight = Math.max(this.viewerHeight, 300);
      
      this.cdr.detectChanges();
    }
  }

  closeDialog(refreshRequired: boolean = false): void {
    this.clearPollingInterval();
    const result: QuickViewDialogResult = {
      refreshRequired: refreshRequired
    };
    this.dialogRef.close(result);
  }

  toggleDrawer(): void {
    this.isDrawerOpen = !this.isDrawerOpen;
    // Recalculate size after drawer state changes and animation completes
    setTimeout(() => this.calculateViewerSize(), 300);
  }

  selectedId: string | undefined;
  private getQueryParamsFromUrl(url: string): { [key: string]: string } {
    try {
      const parsedUrl = new URL(url);
      const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
      const guid = pathSegments[pathSegments.length - 1];
      this.selectedId = guid;
      const searchParams = new URLSearchParams(parsedUrl.search);
      const queryParams: { [key: string]: string } = {};
      searchParams.forEach((value, key) => {
        queryParams[key] = value;
      });
      
      queryParams['quickview'] = "true";
      if (this.data?.showHistory && this.data.showHistory) queryParams['history'] = "true";
      queryParams['id'] = guid;
      return queryParams;
    } catch (error) {
      return {};
    }
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'OK', { duration: 2000 });
  }

  private loadReleaseIssue() {
    if (this.data.releaseIssueId) {
      this.isLoadingIssue = true;
      
      let request = new GetReleaseIssueRequest();
      request.companyId = this.data.companyId;
      request.releaseIssueId = this.data.releaseIssueId;

      this.situViewClient.getReleaseIssue(request)
        .then((response: GetReleaseIssueResponse) => {
          if (response.success && response.releaseIssue) {
            this.releaseIssue = response.releaseIssue;
            
            // Process release items if they exist
            if (this.releaseIssue.releaseItems && this.releaseIssue.releaseItems.length > 0) {
              this.releaseItemsDataSource.data = this.releaseIssue.releaseItems;
            }
            
            this.cdr.detectChanges();
            
            // Initialize sort after the view has been checked
            setTimeout(() => {
              if (this.sort) {
                this.releaseItemsDataSource.sort = this.sort;
              }
            });

            // Auto open the drawer if there are items
            if (this.releaseIssue.releaseItems && this.releaseIssue.releaseItems.length > 0) {
              setTimeout(() => {
                this.isDrawerOpen = false;
                this.cdr.detectChanges();
                // Recalculate size after drawer is open
                setTimeout(() => this.calculateViewerSize(), 300);
              }, 500);
            }
          } else {
            this.showMessage('Failed to load release issue details');
          }
        })
        .catch((error) => {
          console.error('Error loading release issue:', error);
          this.showMessage('Failed to load release issue details');
        })
        .finally(() => {
          this.isLoadingIssue = false;
        });
    }
  }

  viewItem(item: ReleaseItem | undefined, idOverride: string | undefined = undefined): void {
    // Get the correct version to use - prefer standardVersion but fall back to other available versions
    const versionToUse = item?.latestVersion || item?.higherResVersion || idOverride;
    
    if (!versionToUse || !this.data.quickViewLink) {
      this.showMessage('Cannot load item: Missing version information');
      return;
    }
    this.quickViewReportedSituLinkParams = undefined;
    
    try {
      // Extract the base URL pattern from the current quickViewLink
      const currentUrl = new URL(this.data.quickViewLink);
      const pathParts = currentUrl.pathname.split('/');
      
      // Replace the GUID (usually the last part of the path) with the standardVersion
      if (pathParts.length > 0) {
        // Replace the last segment with the version
        pathParts[pathParts.length - 1] = versionToUse;
        
        // Reconstruct the URL
        const newPathname = pathParts.join('/');
        const newUrl = new URL(currentUrl.origin + newPathname + currentUrl.search);
        
        // Create new link
        const newLink = newUrl.toString();
        
        // Update the parameters for the components
        this.quickViewReportedSituLinkParams = this.getQueryParamsFromUrl(newLink);
      }
    } catch (error) {
      this.showMessage('Failed to load item view');
    }
  }

  markFixed(isComplete: boolean) {
    let request = new UpdateReleaseIssueRequest();
    request.companyId = this.data.companyId;
    request.releaseIssueId = this.releaseIssue?.id;
    request.releaseIssueCategory = this.releaseIssue?.releaseIssueCategory;
    request.releaseIssuePriority = this.releaseIssue?.releaseIssuePriority;
    request.releaseIssueStatus = isComplete ? ReleaseIssueStatus.Complete : ReleaseIssueStatus.CompleteNonIssue;
    request.title = this.releaseIssue?.title;
    request.description = this.releaseIssue?.description;
    request.reportedSituLink = this.releaseIssue?.reportedSituLink;
    request.assignedTo = this.releaseIssue?.assignedTo;
    request.assignedToName = this.releaseIssue?.assignedToName;

    this.situViewClient.updateReleaseIssue(request)
      .then((response: UpdateReleaseIssueResponse) => {
        if (response.success) {
          this.closeDialog(true);
        }
      })
      .catch((err: any) => {

      });
  }
  
  checkForUpdates() {
    if (!this.selectedId) {
      this.showMessage("Sorry! There doesn't appear to be a valid home loaded for us to check for updates.");
      return;
    }

    // Set checking state
    this.isChecking = true;
    this.renderStatus = RenderStatus.InProgress;
    
    let getRenderedOutput = new GetSituViewRequest();
    getRenderedOutput.renderedOutputId = this.selectedId;
    getRenderedOutput.includeHistory = true;

    this.situViewClient.getSituView(getRenderedOutput)
      .then((response: GetSituViewResponse) => {
        let fetchLatest = true;
        let laterVersion = "";
        if (response?.success) {
          let currentVersion = response.artifactId;
          if (response?.versionHistory) {
            if (Object.keys(response.versionHistory).length > 1) {
              let sorted = this.sortVersionHistoryByDate(response.versionHistory);
              const latest = sorted[0];
              fetchLatest = latest.version !== currentVersion;
              if (fetchLatest) {
                laterVersion = latest.version;
              }
            }
          }

          if (fetchLatest && laterVersion) {
            // Immediately display the newer version
            this.viewItem(undefined, laterVersion);
            this.renderStatus = RenderStatus.Success;
            this.isChecking = false;
          } else if (fetchLatest) {
            let environment = new ArtifactEnvironment();
            environment.environment = EnvironmentName.Latest;
            
            if (response.productName === ProductName.Facade) {
              environment.product = ProductName.Facade;
              let createFacade = new CreateSituFacadeRequest();
              createFacade.companyId = response.companyId;
              createFacade.environmentOverride = environment;
              createFacade.homeConfigId = response.homeConfigId;
              createFacade.styleConfigTemplateId = response.styleConfigTemplate?.id;
              
              this.situViewClient.createSituFacade(createFacade)
                .then((createFacadeResponse: GetSituFacadeResponse) => {
                  if (createFacadeResponse.success && createFacadeResponse.artifactId) {
                    this.currentRenderingId = createFacadeResponse.artifactId;
                    this.startPolling(ProductName.Facade);
                  } else {
                    this.handleRenderError('Failed to create new situFACADE');
                  }
                })
                .catch((error) => {
                  this.handleRenderError('Error creating situFACADE: ' + error.message);
                });
            } else {
              environment.product = ProductName.Panorama;
              let createView = new CreateSituViewRequest();
              createView.companyId = response.companyId;
              createView.environmentOverride = environment;
              createView.homeConfigId = response.homeConfigId;
              createView.styleConfigTemplateId = response.styleConfigTemplate?.id;
              
              this.situViewClient.createSituView(createView)
                .then((createViewResponse: GetSituViewResponse) => {
                  if (createViewResponse.success && createViewResponse.artifactId) {
                    this.currentRenderingId = createViewResponse.artifactId;
                    this.startPolling(ProductName.Panorama);
                  } else {
                    this.handleRenderError('Failed to create new situVIEW');
                  }
                })
                .catch((error) => {
                  this.handleRenderError('Error creating situVIEW: ' + error.message);
                });
            }
          } else {
            // No update needed
            this.showMessage("Current render is already using the latest settings!");
            this.renderStatus = RenderStatus.Success;
            this.isChecking = false;
          }
        } else {
          this.handleRenderError("Sorry! There doesn't appear to be a valid home loaded for us to check for updates.");
        }
      })
      .catch((error) => {
        this.handleRenderError('Error checking for updates: ' + error.message);
      });
  }

  // Start polling for status
  private startPolling(productType: ProductName): void {
    this.currentPollingAttempt = 0;
    this.clearPollingInterval();
    
    // Set up interval to check render status
    this.pollingInterval = setInterval(() => {
      this.pollRenderStatus(productType);
    }, 5000); // Check every 5 seconds
  }

  // Poll for render status
  private pollRenderStatus(productType: ProductName): void {
    if (!this.currentRenderingId) {
      this.clearPollingInterval();
      this.handleRenderError('No rendering ID to check status');
      return;
    }

    this.currentPollingAttempt++;
    if (this.currentPollingAttempt > this.maxPollingAttempts) {
      this.clearPollingInterval();
      this.handleRenderError('Rendering timed out after 5 minutes');
      return;
    }

    const request = new GetSituViewRequest();
    request.renderedOutputId = this.currentRenderingId;
    
    if (productType === ProductName.Facade) {
      this.situViewClient.getSituFacade(request)
        .then((response: GetSituFacadeResponse) => {
          this.processRenderStatusResponse(response);
        })
        .catch((error) => {
          console.error('Error polling facade status:', error);
          // Continue polling despite errors
        });
    } else {
      this.situViewClient.getSituView(request)
        .then((response: GetSituViewResponse) => {
          this.processRenderStatusResponse(response);
        })
        .catch((error) => {
          console.error('Error polling view status:', error);
          // Continue polling despite errors
        });
    }
  }

  // Process the response from status check
  private processRenderStatusResponse(response: GetSituViewResponse | GetSituFacadeResponse): void {
    if (response.success) {
      // Check for completion status (assuming the API indicates completion somehow)
      // You may need to adjust this based on actual API response structure
      if (response.status === RenderedOutputStatus.Complete) {
        // Rendering complete - load the new view
        this.clearPollingInterval();
        this.renderStatus = RenderStatus.Success;
        this.isChecking = false;
        
        // View the newly rendered item
        if (this.currentRenderingId) {
          this.viewItem(undefined, this.currentRenderingId);
          this.showMessage('Rendering complete! Displaying updated view.');
        }
      }
      // Otherwise continue polling
    } else {
      // Handle unsuccessful response but continue polling
      console.warn('Unsuccessful polling response:', response);
    }
  }

  // Clear polling interval
  private clearPollingInterval(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  // Handle render errors
  private handleRenderError(message: string): void {
    this.clearPollingInterval();
    this.renderStatus = RenderStatus.Failed;
    this.isChecking = false;
    this.showMessage(message);
    this.cdr.detectChanges();
  }

  sortVersionHistoryByDate(versions: { [key: string]: Date } | undefined) {
    if (!versions) {
      return [];
    }
    
    return Object.entries(versions)
      .sort((a, b) => b[1].getTime() - a[1].getTime())
      .map(([version, date]) => ({ version, date }));
  }
}